<template>
  <div>
    <Loader v-if="isFetching" />
    <div class="row">
      <div class="col-12 flex-last">
        <div class="container mt-2">
          <div class="row mb-4 hide-print">
            <div class="col-12">
              <router-link :to="{ name: 'regions' }"><i class="fa fa-angle-left mr-2"></i>Back</router-link>
              <social-sharing
                :url="socialSharing.url"
                :title="socialSharing.title"
                :description="socialSharing.description"
                inline-template
              >
                <div class="social-share">
                  Share:
                  <network network="facebook">
                    <img alt="Facebook" src="/resources/themes/default/images/facebook.svg" />
                  </network>
                  <network network="twitter">
                    <img alt="Twitter" src="/resources/themes/default/images/twitter.svg" />
                  </network>
                </div>
              </social-sharing>
            </div>
          </div>
          <div class="row">
            <div class="col-6 col-lg-1 col-print-2 mb-4 pt-2 pr-0 pl-1">
              <RiskIcon :rating="dangerRating" />
            </div>
            <div
              class="col-12 col-md-6 col-lg-7 mb-md-4 pb-2 pr-0 pr-md-3 pb-md-0 pt-2 pl-0 flex-lg-last text-right hide-print region__public-observations"
            >
              <div class="d-flex align-items-center">
                <div class="col-auto pr-2 ml-auto">
                  <span class="tile__heading tile__heading--title m-0 p-0">
                    Public observations
                    <i class="fa fa-chevron-right" aria-hidden="true"></i>
                  </span>
                </div>

                <div class="col-auto mr-auto mr-md-0">
                  <Button
                    :styles="{
                      'button--public-obs--purple': true,
                      'text-truncate': true
                    }"
                    :onClick="handleObservationButtonClick"
                  >
                    <span slot="label">
                      <PublicMarker
                        :styles="{
                          'd-inline-block': true,
                          'align-middle': true
                        }"
                      />
                      <span class="d-inline-block align-middle">Share now</span>
                    </span>
                  </Button>
                </div>
              </div>

              <div class="d-none d-lg-block">
                <Button
                  class="mt-3"
                  :label="'Print / Download'"
                  :onClick="printPage"
                  :icon="'fa-cloud-download'"
                  :styles="{
                    'button--dark-with-light-outline': true,
                    'button--icon': false
                  }"
                />

                <Button
                  class="mt-2"
                  :label="'Subscribe to updates'"
                  :onClick="toggleSubscribe"
                  :icon="'fa-envelope'"
                  :styles="{
                    'button--dark-with-light-outline': true,
                    'button--icon': false
                  }"
                />
              </div>
            </div>
            <div
              class="col-5 col-lg-3 offset-lg-3 offset-1 mb-md-4 pb-2 pr-md-3 pb-md-0 pt-2 pl-0 flex-lg-last text-right hide-print d-lg-none"
            >
              <button
                type="button"
                class="map-control public-observation__map-btn"
                @click.prevent="map_show = !map_show"
              >
                {{ map_show ? "Hide" : "Show" }} Map
              </button>
            </div>
            <div class="col-12 col-lg-4 col-print-10">
              <h1 class="font-weight-bold">{{ selectedRegion.title }}</h1>
              <RiskPill :rating="dangerRating" />
              <p
                class="font-weight-bold"
                v-if="selectedForecast && selectedForecast.importantInformation"
                v-html="selectedForecast.importantInformation"
              ></p>
            </div>
          </div>
          <div v-if="selectedForecast.created" class="row mt-4">
            <div class="col-12 col-print-6">
              <p>Issued {{ selectedForecast.created | niceDate }}</p>
            </div>
            <div class="col-12 col-print-6">
              <p>
                <strong>Valid until {{ validForecastEndDateString | niceDate }}</strong>
              </p>
            </div>
          </div>

          <div class="d-lg-none">
            <Button
              class="mb-3"
              :label="'Print / Download'"
              :onClick="printPage"
              :icon="'fa-cloud-download'"
              :styles="{
                'button--dark-with-light-outline': true,
                'button--icon': false
              }"
            />

            <Button
              class="mb-3"
              :label="'Subscribe to updates'"
              :onClick="toggleSubscribe"
              :icon="'fa-envelope'"
              :styles="{
                'button--dark-with-light-outline': true,
                'button--icon': false
              }"
            />
          </div>
        </div>
      </div>
      <div class="col-12 pr-0 pl-0 hide-print">
        <Mapbox
          :class="map_show ? 'map-shown' : ''"
          :styles="{ 'map-wrapper--strip': true }"
          :hidden="false"
          :expanded="true"
          :closable="false"
          :regions="regions"
          :forecasts="forecasts"
          :observations="observations"
          :selectedRegion="selectedRegion.id"
          :onLoad="handleMapLoad"
          :onRegionSelect="handleRegionSelect"
          :onObservationSelect="handleObservationSelect"
        />
      </div>
    </div>
    <div class="break-avoid">
      <div class="mt-4 mb-0 background-lightest-grey">
        <div
          v-for="(danger, index) in altitudeDangers"
          :key="index"
          :class="{
            container: true,
            'border-bottom-lighter-grey': index !== 2
          }"
        >
          <AltitudeDangerRow
            :rating="danger.rating"
            :level="index"
            :altitudeTo="danger.altitudeTo"
            :altitudeFrom="danger.altitudeFrom"
          />
        </div>
      </div>
      <div class="container">
        <div class="row pt-5 pb-5">
          <div class="col-12">
            <DangerScale />
          </div>
        </div>
      </div>
    </div>
    <div class="container" v-if="selectedForecast && selectedForecast.additionalInformation">
      <div 
        v-for="(danger, index) in avalancheDangers" 
        v-if="danger" 
        class="row break-avoid" 
      >
        <div class="col-12 break-avoid">
          <AvalancheDangerRow
            :priority="danger.priority"
            :priority_level="danger.priority_level"
            :character="danger.character"
            :description="danger.description"
            :time="danger.time"
            :trend="danger.trend"
            :moreInfoLink="cms.dangerScaleLink"
            :aspects="danger.aspects"
            :likelihood="danger.likelihood"
            :size="Math.round(parseFloat(danger.size))"
          />
        </div>
      </div>


      <!-- Additional information: Recent Avalanche Activity, Currenty Snowpack Conditions, etc. -->
      <div 
        v-for="(info, index) in selectedForecast.additionalInformation" 
        class="row additional-info break-avoid" 
        :key=index
      >
        <div class="col-12 mb-4">
          <div class="border-top-light-grey"></div>
        </div>
        <div class="col-12 col-lg-2 col-print-2">
          <img class="img-fluid mb-3" :src="info.icon" :alt="info.title" />
        </div>
        <div class="col-12 col-lg-10 col-print-10">
          <h2 :class="{ 'additional-info__header': true }">
            {{ info.title }}
          </h2>
          <div class="mb-4">
            <div v-html="info.content"></div>

            <!-- Forecast images below -->
            <div 
              class="additional-info__image-container d-flex" 
              v-if="(info.forecastImages && info.forecastImages.length)"
            >
              <button
                v-for="(image, index) in info.forecastImages" 
                :key="index"
                @click="handleImageClick(image)"
                class="additional-info__image-btn"
              >
                <img 
                  class="additional-info__image-image"
                  :src="image.thumbnailLink"
                  :alt="image.caption"
                />
              </button>
            </div>

            <p v-if="info.title === 'Mountain Weather' && selectedForecast.metserviceForecastLink">
              <img
                class="mr-3 pb-1 d-block d-lg-inline"
                src="/resources/themes/default/images/metservice-logo.png"
                alt="MetService"
                width="170px"
              />
              for more information go to
              <a :href="selectedForecast.metserviceForecastLink" target="_blank">{{
                selectedForecast.metserviceForecastLink
              }}</a>
            </p>
          </div>
        </div>
      </div>

      <p v-if="selectedForecast.forecaster" class="text-light border-top-light-grey pt-2 mb-4">
        Forecast by {{ selectedForecast.forecaster }}
      </p>
    </div>
    <div class="container mb-5">
      <div class="previous-forecast">
        <input type="date" :min="minForecastDate" :max="maxForecastDate" v-model="forecastDate" class="form-control" />
        <Button :label="'View Forecast'" :onClick="viewPreviousForecast" />
        <div v-if="previousForecastMessage" class="previous-forecast-message">
          {{ previousForecastMessage }}
        </div>
      </div>

      <div
        class="subsform subsform--small row background-lightest-grey pt-2 pb-2 mt-2"
        v-if="subscribeOpen"
        ref="subscribeBox"
      >
        <div class="col-12">
          <h2 class="h3 mt-1">Subscribe to updates</h2>
        </div>
        <div class="col-12 col-lg-5">
          <label>Your Name</label>
          <input type="text" v-model="subscriberName" maxlength="255" />
        </div>
        <div class="col-12 col-lg-5">
          <label>Email address</label>
          <input type="email" v-model="subscriberEmail" maxlength="255" />
        </div>
        <div class="col-12 col-lg-2 subsform__submit-col">
          <Button :styles="{ 'button--dark-with-light-outline': true }" label="Subscribe now" :onClick="subscribe" />
        </div>
        <div class="col-12 mt-2">
          <label class="custom-radio-button">
            <input type="checkbox" v-model="publicObsSubscribed" />
            <span></span>I would also like to subscribe to the public observtions for {{ selectedRegion.title }}
          </label>
        </div>
      </div>

      <div class="subsform row background-lightest-grey p-4 mt-2" v-if="subscribeSuccess">
        <p>You have subscribed to email updates for {{ selectedRegion.title }}.</p>
      </div>
    </div>

    <vue-easy-lightbox 
      :imgs="[{
        src: selectedImageLink, 
        title: selectedImageCaption
      }]"
      :visible="lightboxVisible"
      @hide="lightboxVisible = false"
    >
      <template v-slot:title v-if="selectedImageCaption">
        <div class="vel-img-title">
          <span class="vel-img-title-text">{{ selectedImageCaption }}</span>
        </div>
      </template>
    </vue-easy-lightbox>
  </div>
</template>

<script>
import Loader from "../components/loader/Loader";
import AltitudeDangerRow from "../components/altitude-danger-row/AltitudeDangerRow";
import AvalancheDangerRow from "../components/avalanche-danger-row/AvalancheDangerRow";
import bbox from "@turf/bbox";
import Button from "../components/button/Button";
import DangerScale from "../components/danger-scale/DangerScale";
import DatesMixin from "../mixins/dates";
import Mapbox from "../components/map/Map";
import { mapState, mapGetters } from "vuex";
import RiskIcon from "../components/risk-icon/RiskIcon";
import RiskPill from "../components/risk-pill/RiskPill";
import API from "../api/API";
import moment from "moment";
import PublicMarker from "../components/public-marker/PublicMarker.vue";
import VueEasyLightbox from "vue-easy-lightbox";

export default {
  components: {
    Loader,
    AltitudeDangerRow,
    AvalancheDangerRow,
    Button,
    DangerScale,
    Mapbox,
    RiskIcon,
    RiskPill,
    PublicMarker,
    VueEasyLightbox
  },

  mixins: [DatesMixin],

  data() {
    return {
      map_show: false,
      subscribeOpen: false,
      subscribeSuccess: false,
      subscriberName: "",
      subscriberEmail: "",
      forecastDate: "",
      previousForecast: null,
      previousForecastMessage: false,
      isFetching: false,
      publicObsSubscribed: false,
      lightboxVisible: false,
      selectedImageLink: '',
      selectedImageCaption: '',
    };
  },

  watch: {
    map_show(nv, ov) {
      setTimeout(function() {
        window.dispatchEvent(new Event("resize"));
      }, 200);
    }
  },

  mounted() {
    this.forecastDate = this.maxForecastDate;

    this.resize();
    window.addEventListener("resize", this.resize);
  },

  computed: {
    ...mapState({
      cms: state => state.cms,
      regions: state => state.regions.filter(region => region.geometry !== null),
      selectedRegion: state => state.selectedRegion,
      forecasts: state => state.forecasts
    }),

    ...mapGetters(["forecastByRegion"]),

    minForecastDate() {
      if (this.selectedRegion.firstForecast) {
        return this.selectedRegion.firstForecast.split(" ")[0];
      }

      return null;
    },

    maxForecastDate() {
      return this.forecastByRegion(this.selectedRegion.id)
        ? this.forecastByRegion(this.selectedRegion.id).created.split(" ")[0]
        : null;
    },

    selectedForecast() {
      if (this.previousForecast) {
        return this.previousForecast;
      }

      return this.forecastByRegion(this.selectedRegion.id) || {};
    },

    dangerRating() {
      let rating = 0;
      if (this.selectedForecast && this.selectedForecast.altitudeDanger) {
        this.selectedForecast.altitudeDanger.forEach(function(danger) {
          if (parseInt(danger.rating, 10) > rating) {
            rating = danger.rating;
          }
        });
      }
      return parseInt(rating, 10);
    },

    altitudeDangers() {
      return this.selectedForecast ? this.selectedForecast.altitudeDanger : [{}, {}, {}];
    },

    avalancheDangers() {
      return this.selectedForecast ? this.selectedForecast.avalancheDangers : [{}, {}, {}];
    },

    validForecastEndDateString() {
      if (!this.selectedForecast || !this.selectedForecast.validPeriod) {
        return "";
      }

      let hours;

      switch (this.selectedForecast.validPeriod) {
        case "72hrs":
          hours = 72;
          break;
        case "48hrs":
          hours = 48;
          break;
        default:
          hours = 24;
      }

      const m = moment(this.selectedForecast.created);

      // Add hours to the lasted edited date.
      m.add(hours, "hours");

      return m.format("YYYY-MM-DD HH:mm:ss");
    },

    observations() {
      return this.$store.getters.recentObservationsGeoJson();
    },

    socialSharing() {
      let socialDescription = "";

      if (this.selectedRegion.socialDescription && this.selectedRegion.socialDescription.length > 0) {
        socialDescription = this.selectedRegion.socialDescription;
      } else if (this.cms.socialDescription && this.cms.socialDescription.length > 0) {
        socialDescription = this.cms.socialDescription;
      } else {
        socialDescription = `Planning to visit the ${this.selectedRegion.title} region? Check the Avalanche forecast from the New Zealand Mountain Safety Council.`;
      }

      return {
        url: window.location.href,
        title: `${this.selectedRegion.title} Forecast | NZ Avalanche Advisory`,
        description: this.cms.socialDescription
      };
    }
  },

  methods: {
    handleImageClick(image) {
      this.selectedImageLink = image.link;
      this.selectedImageCaption = image.caption;
      this.lightboxVisible = !this.lightboxVisible;
    },

    handleMapLoad(map) {
      map.setPaintProperty(`region-${this.selectedRegion.id}-fill`, "fill-opacity", 0);
      map.fitBounds(bbox(this.selectedRegion.geometry.layers[0]), {
        padding: 20
      });
    },

    handleRegionSelect(region) {
      this.$store.dispatch("setSelectedRegion", region);
      this.$router.push({ name: "region", params: { id: region.id } });
    },

    toggleSubscribe() {
      this.subscribeOpen = true;
      this.subscribeSuccess = false;
      this.$nextTick(() => {
        this.$refs.subscribeBox.scrollIntoView({ behavior: "smooth" });
      });
    },

    printPage() {
      let url = `/forecast/pdf/${this.selectedForecast.id}`;
      window.open(url);
    },

    subscribe() {
      API.submit("subscription", {
        name: this.subscriberName,
        email: this.subscriberEmail,
        subscriptions: [this.selectedRegion.id],
        subscribe_to_public_obs: this.publicObsSubscribed
      }).then(() => {
        this.subscribeSuccess = true;
        this.subscribeOpen = false;
      });
    },

    handleObservationSelect(id) {
      this.$store.dispatch("setSelectedObservation", this.$store.getters.observationById(id));
      this.$router.push({ name: "observation", params: { id } });
    },

    viewPreviousForecast() {
      this.isFetching = true;
      this.previousForecastMessage = false;

      API.fetch("forecastsearch", undefined, {
        date: this.forecastDate,
        region: this.selectedRegion.id
      }).then(response => {
        if (response && response.data && response.data.forecast) {
          this.previousForecast = response.data.forecast;

          window.scrollTo(0, 0);
        } else {
          this.previousForecastMessage = "Forecast could not be found";
        }

        this.isFetching = false;
      });
    },

    handleObservationButtonClick(event) {
      this.$router.push({ name: "observationform" });
    },

    resize() {
      if (window.matchMedia("(min-width: 768px)").matches) {
        document.getElementsByTagName("body")[0].style["padding-bottom"] = "0";
      } else {
        document.getElementsByTagName("body")[0].style["padding-bottom"] = "59px";
      }
    }
  },

  head: {
    meta: function() {
      let socialDescription = "";

      if (this.selectedRegion.socialDescription && this.selectedRegion.socialDescription.length > 0) {
        socialDescription = this.selectedRegion.socialDescription;
      } else if (this.cms.socialDescription && this.cms.socialDescription.length > 0) {
        socialDescription = this.cms.socialDescription;
      } else {
        socialDescription = `Planning to visit the ${this.selectedRegion.title} region? Check the Avalanche forecast from the New Zealand Mountain Safety Council.`;
      }

      let tags = [
        {
          property: "og:title",
          content: `${this.selectedRegion.title} Forecast | NZ Avalanche Advisory`
        },
        { property: "og:description", content: socialDescription },
        { property: "og:image", content: this.selectedRegion.socialImage },
        { property: "og:image:width", content: 300 },
        { property: "og:image:height", content: 300 },
      ];

      if (this.selectedRegion.longitude > 0 && this.selectedRegion.latitude > 0) {
        tags.push(
          { property: "og:type", content: "place" },
          {
            property: "place:location:longitude",
            content: this.selectedRegion.longitude
          },
          {
            property: "place:location:latitude",
            content: this.selectedRegion.latitude
          }
        );
      }

      return tags;
    },
    title: function() {
      return {
        inner: this.selectedRegion.title
      };
    }
  },

  created() {
    // If there's no selected region (the user has not come via the homepage or has hard reloaded)
    // set the selected region from the URL.
    if (!this.selectedRegion.id) {
      const id = parseInt(this.$route.params.id, 10);
      this.$store.dispatch("setSelectedRegion", this.$store.getters.regionById(id));
    }
  }
};
</script>
